import clsx from 'clsx'
import { HTMLAttributes } from 'react'
import TokenBalance from './components/TokenBalance/TokenBalance'
import { AnyToken } from './types/models/Tokens'
import NumericField from './ui-kit/NumericField/NumericField'
import {
  NumericFieldMode,
  NumericFieldProps,
  NumericFieldTypes,
  NumericFieldValue,
} from './ui-kit/NumericField/types'
import TokenIcon from './ui-kit/TokenIcon/TokenIcon'
import { displayAmount } from './utils/formatters/number'
import Badge from './ui-kit/Badge'
import { isNil } from 'lodash'
import { useTranslation } from 'react-i18next'
import useSmartAccount from './api-clients/safe-protocol-kit/useSmartAccount'

interface TradeTokenDisplayProps<T extends NumericFieldTypes>
  extends HTMLAttributes<HTMLDivElement> {
  token?: AnyToken
  amount: NumericFieldValue<T>
  label: string
  readonly?: boolean
  numericFieldProps?: Omit<NumericFieldProps<T>, 'value'>
  maxValue?: NumericFieldValue<T> | null
}

const TokenDisplay = <T extends NumericFieldTypes>({
  token,
  amount,
  label,
  className,
  readonly = false,
  numericFieldProps,
  maxValue,
  ...rest
}: TradeTokenDisplayProps<T>) => {
  const { t } = useTranslation('common')
  const { smartAccountInfo } = useSmartAccount()
  const mode: NumericFieldMode = token?.type === 'ERC20' ? 'float' : 'integer'

  const maxValuePassed = !isNil(maxValue)
  const maxIsActive =
    maxValuePassed && numericFieldProps?.type === 'big'
      ? (amount as Big).eq(maxValue as Big)
      : amount === maxValue

  const handleMaxAmount = () => {
    if (maxValuePassed) {
      numericFieldProps?.onChange?.(maxValue)
    }
  }

  return (
    <div
      className={clsx(
        'flex justify-between rounded-md p-2 bg-secondary',
        className
      )}
      {...rest}
    >
      <div className='flex flex-col gap-1'>
        <div className='typo-label1'>{label}</div>
        {readonly ? (
          <div className='typo-title3 font-medium text-main-accent'>
            {displayAmount(amount)}
          </div>
        ) : (
          <NumericField
            className='col-start-5 col-span-8 pr-0'
            mode={mode}
            value={amount}
            maxDecimals={token?.decimals}
            variant='text'
            size='lg'
            align='left'
            hideError
            inputProps={{
              placeholder: t('tokenDisplay.placeholder'),
              className: 'bg-transparent',
              style: { padding: 0 },
            }}
            {...numericFieldProps}
          />
        )}
        {maxValuePassed && (
          <Badge
            style={{
              borderColor: maxIsActive ? undefined : 'transparent',
            }}
            color='secondary'
            active={maxIsActive}
            size='sm'
            onClick={handleMaxAmount}
          >
            {t('tokenDisplay.max')}
          </Badge>
        )}
      </div>

      <div className='flex flex-col justify-between items-end gap-2'>
        <div className='flex items-center gap-2'>
          <TokenIcon token={token} />
          <div>{token?.symbol}</div>
        </div>
        <TokenBalance
          token={token}
          ownerAddress={smartAccountInfo?.address}
          StartAdornment={<span>{t('tokenDisplay.balance')}:</span>}
        />
      </div>
    </div>
  )
}

export default TokenDisplay
